// TB-061 (E5): the doctor an establishment is managing ("View Details" on My
// Doctors). Kept in sessionStorage, so each tab has its own selection, and
// scoped to the signed-in establishment, so a selection made by another
// account in the same tab is never reused.

const SESSION_KEY = 'tb.estDoctorSelection'

// Legacy keys. TB-080 (DEF-175) moved the last readers (Home, Patients,
// Consultation, BookAppoinment) to getSelectedEstDoctorId(), so the selection
// is no longer mirrored into localStorage. clear still removes them so a value
// left by an older build cannot linger.
const LEGACY_ID_KEY = 'establishmentProviderID'
const LEGACY_NAME_KEY = 'estProviderName'

// Same lookup the app uses: localStorage 'user', id as uid (login) or userId (sign-up)
export function currentUserId () {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null')
    if (!user) return null
    const id = user.uid || user.userId || user.id || user.email
    return id ? String(id) : null
  } catch (e) {
    return null
  }
}

// asDoctor: the clinic is managing this doctor as the doctor ("View Details",
// "Manage as doctor"), so the nav switches to the doctor view. Without it the
// selection only picks the doctor for a clinic page (Schedules, Create slot).
export function setSelectedEstDoctor ({ id, name, asDoctor = false } = {}) {
  if (!id) return
  const record = {
    estId: currentUserId(),
    providerId: String(id),
    name: name || ''
  }
  if (asDoctor) record.asDoctor = true
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(record))
  } catch (e) {
    // storage blocked (private mode, quota): selection is best effort
  }
  try {
    localStorage.removeItem(LEGACY_ID_KEY)
    localStorage.removeItem(LEGACY_NAME_KEY)
  } catch (e) {
    // storage blocked (private mode, quota): nothing to clean up
  }
}

function readRecord () {
  try {
    const record = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null')
    if (!record || !record.providerId) return null
    if (!record.estId || record.estId !== currentUserId()) return null
    return record
  } catch (e) {
    return null
  }
}

export function getSelectedEstDoctorId () {
  const record = readRecord()
  return record ? record.providerId : null
}

export function getSelectedEstDoctorName () {
  const record = readRecord()
  return record ? record.name : ''
}

// TB-085 (DEF-229): the "establishment viewing a doctor" state for this tab, in
// the shape the nav used to read from the shared localStorage
// estProviderDetails. Null (clinic mode) when this tab has no selection for the
// signed-in establishment; another tab's doctor is never used as a fallback.
// W7: a clinic-mode selection (Schedules picker, TB-093) is not a doctor view.
export function getEstProviderView () {
  const record = readRecord()
  if (!record || !record.asDoctor) return null
  return { estDoctorId: record.providerId, name: record.name, userRole: 'estProvider' }
}

export function isEstProviderView () {
  return !!getEstProviderView()
}

export function clearSelectedEstDoctor () {
  try {
    sessionStorage.removeItem(SESSION_KEY)
  } catch (e) {
    // storage blocked (private mode, quota): selection is best effort
  }
  try {
    localStorage.removeItem(LEGACY_ID_KEY)
    localStorage.removeItem(LEGACY_NAME_KEY)
  } catch (e) {
    // storage blocked (private mode, quota): selection is best effort
  }
}

// TB-081 (DEF-218): leave the "establishment viewing a doctor" mode, the same
// steps as "Back to establishment" (Dashboard.vue / profile menu), so the nav,
// user card and top bar switch back to the clinic. Returns true when a doctor
// view was active.
export function exitEstDoctorView () {
  let active = isEstProviderView()
  try {
    const est = JSON.parse(localStorage.getItem('estProviderDetails') || 'null')
    active = active || !!(est && est.userRole === 'estProvider')
    localStorage.removeItem('estProviderDetails')
    localStorage.removeItem('navigation')
  } catch (e) {
    // storage blocked (private mode, quota): selection is best effort
  }
  clearSelectedEstDoctor()
  if (active) {
    try { window.dispatchEvent(new Event('estProviderReady')) } catch (e) { /* event is best effort */ }
  }
  return active
}

// TB-081 (DEF-218): the clinic dashboard always opens in clinic mode.
export function shouldExitEstDoctorView (toName, role) {
  return toName === 'doctor-establishment-doctor' && role === 'establishment'
}

// TB-086 (DEF-240): pages that work on one doctor (Schedules / Calendar View,
// Create slot) need a selected doctor. Opened by an establishment without one
// (side menu "Schedules", a bookmark, a new tab) they called the API with
// providerId=null and showed raw server errors. They now send the clinic to its
// doctors list with this hint instead.
export const EST_PICK_DOCTOR_MESSAGE =
  'Choose a doctor first: open View Details on one of your doctors to see their schedule.'

export function estDoctorsListLocation () {
  return { path: '/doctor/establishment-doctor', query: { view: 'doctors' } }
}

// True when an establishment opens a per-doctor page with no doctor selected.
export function needsEstDoctorSelection (role) {
  return role === 'establishment' && !getSelectedEstDoctorId()
}
